import { lazy, Suspense, useCallback } from "react";
import { createPortal } from "react-dom";
import { useI18n } from "@expertloop/i18n/react";
import type { PdfViewerProps } from "./types";
import "./PdfViewer.css";

// Le moteur (PDF.js et son worker, plusieurs centaines de Ko) n'est chargé
// qu'à la première ouverture d'un document.
const PdfViewerContent = lazy(() => import("./PdfViewerContent"));

type PublicProps = Omit<PdfViewerProps, "onDownload"> & {
	/** Remplace le téléchargement par défaut (ouverture du lien signé). */
	onDownload?: () => void;
};

/**
 * Lecteur de PDF plein écran, sans quitter la conversation.
 *
 * Rendu dans le `body` : une bulle de message peut posséder un contexte
 * d'empilement (transformation, découpage) qui emprisonnerait un calque en
 * position fixe et l'empêcherait de couvrir l'écran.
 */
export function PdfViewer({ url, fileName, onClose, onDownload }: PublicProps) {
	const { t } = useI18n();

	const handleDownload = useCallback(() => {
		if (onDownload) {
			onDownload();
			return;
		}
		const link = document.createElement("a");
		link.href = url;
		link.download = fileName;
		link.target = "_blank";
		link.rel = "noopener noreferrer";
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	}, [onDownload, url, fileName]);

	return createPortal(
		<Suspense
			fallback={
				<div className="el-pdf-viewer el-pdf-viewer--loading" role="status" aria-label={t("pdfViewer.loading")}>
					<i className="fas fa-spinner fa-spin" aria-hidden="true"></i>
				</div>
			}
		>
			<PdfViewerContent url={url} fileName={fileName} onClose={onClose} onDownload={handleDownload} />
		</Suspense>,
		document.body,
	);
}
